/**
 *  implements auth and identity workflow using kratos
 */
class Identity {
    /**
     *
     * @param kratosUrl - redefines default kratos endpoints url
     */
    constructor(kratosUrl) {
        this.kratosUrl = kratosUrl ? kratosUrl : window.location.origin + "/identity/";
        this.endpoints = {
            login:      "self-service/login/browser",
            logout:     "self-service/logout/browser",
            register:   "self-service/registration/browser",
            whoami:     "sessions/whoami",
            errors:     "self-service/errors"
        }
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };
        this.identity = null;
        this.flow = null;
    }

    async _whoami() {
        let r = await this._request(new URL(this.endpoints.whoami, this.kratosUrl))
        if (r.data.identity) {
            this.identity = r.data.identity;
        }
        return this.identity;
    }

    //get error by id
    async _error(id) {
        let r = await this._request(new URL(this.endpoints.errors + '?id=' + id, this.kratosUrl))
/*
        if (r.error) {
            this.elog("ERROR", `Something get wrong while getting kratos error details: ${r.error.message}`, r.error);
            return null
        }
 */
        return r.error;
    }

    async _loginFlow() {
        let r = await this._request(new URL(this.endpoints.login, this.kratosUrl));
        if (r.data.id) {
            this.flow = r.data;
        } else if (r.error) {
            this.elog("ERROR", `Can't start login flow: ${r.error.message}`, r.error);
        }
        return this.flow;
    }

    async _registrationFlow() {
        let r = await this._request(new URL(this.endpoints.register, this.kratosUrl));
        if (r.data.id) {
            this.flow = r.data;
        } else if (r.error) {
            this.elog("ERROR", `Can't start registration flow: ${r.error.message}`, r.error);
        }
        return this.flow;
    }

    _getAttribute(nodes, attributeName) {
        if (! nodes)
            return undefined;
        return nodes.find( item => item.attributes.name === attributeName)
    }

    /**
     *
     * @param url - URL object
     * @param options - fetch options
     * @returns {Promise<{data: {}, error: null, status: number}>}
     * @private
     */
    async _request(url, options) {
        options = options ? options : {};
        options.credentials = 'include';
        options.headers = options.headers ? options.headers : {};
        options.headers["Accept"] = "application/json";
        options.headers["Content-Type"] = "application/json";

        if (this.flow && this.flow.ui) {
            options.headers["X-CSRF-Token"] = this._getAttribute(this.flow.ui.nodes, "csrf_token").attributes.value;
        }

        let r = {
            status: 0,
            data: {},
            error: null
        }
        let response = await fetch(url.toString(), options)
            .catch( error => {r.error = {message: error.toString()}; return null});
        if (response) {
            r.status = response.status
            r.data = await response.json()
                .catch( error => {r.error = {message: error.toString(), code: response.status}; return {}})
            if (r.data && r.data.error) {
                r.error = r.data.error
            }
        }
        if ( ! r.status || r.status === 502 ) {
            this.elog("WARN", `kratos isn't available (${r.status}): ${r.error.message}`);
        }
        return r
    }

    /**
     * getIdentity checks sessions cookies and restore user profile object
     * @returns {Promise<*>}
     */
    async getIdentity() {
        return this.identity != null ? this.identity : this._whoami();
    }


    /**
     * getError return error description which was happened during kratos workflows
     * usually these are oAuth errors that occurred during authorization at third party web sites (google, facebook, etc)
     * kratos does redirect to our error page and we can get error description with this method
     * @param id
     * @returns {Promise<*>}
     */
    async getError(id) {
        return id != null && id !== "" ? this._error(id) : null
    }

    /**
     * doRegistrationGoogle process a new account registration using google account
     * on succ it doesn't return and performs browser redirect to provider auth page
     * returns on error
     * @param provider - provider name (for example: google, facebook etc)
     * @returns {Promise<void>}
     */
    async doRegistrationOID(provider) {
        let flow = await this._registrationFlow();
        if (flow == null) {
            return
        }
        let r = await this._request(new URL(flow.ui.action),
            {method: "POST", body: JSON.stringify({method: "oidc", provider: provider}),
            });
        if (r.data && r.data.redirect_browser_to) {
            window.location.href = r.data.redirect_browser_to;
        } else {
            let errMsg = r.error && r.error.message ? ": " + r.error.message : "";
            this.elog("WARN", `Expected redirect to openID provider's endpoint, but got the error${errMsg}`);
        }
    }

    /**
     * doLoginOID process login using openId provider (google, facebook, etc)
     * on succ it doesn't return and performs browser redirect to provider auth page
     * returns on error
     * @param provider - provider name (for example: google, facebook, etc)
     * @returns {Promise<void>}
     */
    async doLoginOID(provider) {
        let flow = await this._loginFlow();
        if (flow == null) {
            return
        }
        let r = await this._request(new URL(flow.ui.action),
            {method: "POST", body: JSON.stringify({method: "oidc", provider: provider}),
            });
        if (r.data && r.data.redirect_browser_to) {
            window.location.href = r.data.redirect_browser_to;
        } else {
            let errMsg = r.error && r.error.message ? ": " + r.error.message : "";
            this.elog("WARN", `Expected redirect to openID provider's endpoint, but got the error ${errMsg}`);
        }
    }

    /**
     * doLogout process logout
     * @returns {Promise<boolean>} true/false on success or not
     */
    async doLogout() {
        let r = await this._request(new URL(this.endpoints.logout, this.kratosUrl));
        if (r.status === 401) {
            return true; //isn't authorized already
        }
        if (r.error) {
            let errMsg = r.error && r.error.message ? ": " + r.error.message : "";
            this.elog("WARN", `Expected logout token, but got the error${errMsg}`);
            return false
        }
        r = await this._request(r.data.logout_url);
        if (r.status !== 204) {
            let errMsg = r.error && r.error.message ? ": " + r.error.message : "";
            this.elog("WARN", `Expected logout, but got the error${errMsg}`);
            return false
        }
        this.identity = null;
        return true
    }
}

export { Identity };
